import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import CountryCodePicker from "@/components/CountryCodePicker";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo.png";
import { Loader2, Eye, EyeOff, Home, CheckCircle, Check, X, AlertTriangle } from "lucide-react";
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from "@/lib/countryCodes";
import SEO from "@/components/SEO";

const capitalizeName = (name: string) =>
  name
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");

type AuthView = "signIn" | "signUp" | "forgotPassword" | "resetPassword";

// === Client-side rate limiting (per browser) ===
const RATE_LIMIT_KEY = "auth_rate_limit";
const MAX_ATTEMPTS = 5;
const WINDOW_MS = 15 * 60 * 1000; // 15 minutes
const LOCKOUT_MS = 15 * 60 * 1000;

const getRateState = (): { attempts: number[]; lockedUntil: number } => {
  try {
    const raw = localStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return { attempts: [], lockedUntil: 0 };
    return JSON.parse(raw);
  } catch {
    return { attempts: [], lockedUntil: 0 };
  }
};

const recordAttempt = () => {
  const state = getRateState();
  const now = Date.now();
  const recent = state.attempts.filter((t) => now - t < WINDOW_MS);
  recent.push(now);
  const lockedUntil = recent.length >= MAX_ATTEMPTS ? now + LOCKOUT_MS : 0;
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({ attempts: recent, lockedUntil }));
  return lockedUntil;
};

const clearAttempts = () => localStorage.removeItem(RATE_LIMIT_KEY);

const isLockedOut = (): number => {
  const state = getRateState();
  return state.lockedUntil > Date.now() ? state.lockedUntil : 0;
};

// === Password strength ===
type PasswordChecks = {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
};

const evaluatePassword = (pwd: string): PasswordChecks => ({
  length: pwd.length >= 8,
  uppercase: /[A-Z]/.test(pwd),
  lowercase: /[a-z]/.test(pwd),
  number: /[0-9]/.test(pwd),
});

const passwordStrength = (checks: PasswordChecks): number =>
  Object.values(checks).filter(Boolean).length;

const Auth = () => {
  const location = useLocation();
  const [view, setView] = useState<AuthView>(location.state?.isSignUp ? "signUp" : "signIn");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phoneLocal, setPhoneLocal] = useState("");
  const [form, setForm] = useState({ fullName: "", email: "", password: "" });
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState<number>(0);
  const [signupEmailSent, setSignupEmailSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get redirect target from query string
  const redirectTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get("redirect");
    if (target && target.startsWith("/") && !target.startsWith("//")) return target;
    return "/dashboard";
  }, [location.search]);

  // Listen for password recovery event + check existing lockout
  useEffect(() => {
    setLockoutUntil(isLockedOut());
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setView("resetPassword");
      }
    });

    const hash = window.location.hash;
    if (hash && hash.includes("type=recovery")) {
      setView("resetPassword");
    }

    return () => subscription.unsubscribe();
  }, []);

  // Live tick the lockout countdown
  useEffect(() => {
    if (!lockoutUntil) return;
    const interval = setInterval(() => {
      if (Date.now() >= lockoutUntil) {
        setLockoutUntil(0);
        clearAttempts();
      } else {
        setLockoutUntil((prev) => prev); // trigger re-render
        // force render
        setForm((f) => ({ ...f }));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const passwordChecks = evaluatePassword(form.password);
  const pwdStrength = passwordStrength(passwordChecks);
  const isPasswordValid = pwdStrength === 4;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.startsWith("0")) value = value.slice(1);
    setPhoneLocal(value.slice(0, 10));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, fullName: capitalizeName(e.target.value) }));
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast({ title: "Please enter your email address", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await supabase.auth.resetPasswordForEmail(form.email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      // Always show success (prevents email enumeration)
      setResetEmailSent(true);
      toast({
        title: "Check your inbox",
        description: "If an account exists for that email, a reset link has been sent.",
      });
    } catch {
      // Still show generic success
      setResetEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!form.email.trim()) {
      toast({ title: "Please enter your email first", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await supabase.auth.resend({ type: "signup", email: form.email });
      toast({
        title: "Verification email sent",
        description: "If an unverified account exists, a new link has been sent.",
      });
    } catch {
      toast({
        title: "Verification email sent",
        description: "If an unverified account exists, a new link has been sent.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }
    const checks = evaluatePassword(newPassword);
    if (passwordStrength(checks) < 4) {
      toast({
        title: "Weak password",
        description: "Use at least 8 characters with uppercase, lowercase, and a number.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setResetSuccess(true);
      toast({ title: "Password updated successfully!" });
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check lockout
    const locked = isLockedOut();
    if (locked) {
      setLockoutUntil(locked);
      const minutes = Math.ceil((locked - Date.now()) / 60000);
      toast({
        title: "Too many attempts",
        description: `Please wait ${minutes} minute(s) before trying again.`,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const fullPhone = countryCode + phoneLocal;

    try {
      if (view === "signUp") {
        if (!form.fullName.trim() || !phoneLocal.trim()) {
          toast({ title: "Please fill in all fields", variant: "destructive" });
          setLoading(false);
          return;
        }
        if (!isPasswordValid) {
          toast({
            title: "Weak password",
            description: "Use at least 8 characters with uppercase, lowercase, and a number.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        const refCode = new URLSearchParams(window.location.search).get("ref");

        const { data: signUpData, error } = await supabase.auth.signUp({
          email: form.email,
          password: form.password,
          options: {
            data: { full_name: form.fullName.trim(), phone: fullPhone, referral_code: refCode || null },
            emailRedirectTo: `${window.location.origin}${redirectTo}`,
          },
        });

        if (error) throw error;

        // Track referral
        if (refCode && signUpData.user) {
          const { data: referrerId } = await (supabase as any)
            .rpc("resolve_referral_code", { _code: refCode.toUpperCase() });
          const refRow = referrerId ? { user_id: referrerId as string } : null;
          if (refRow?.user_id && refRow.user_id !== signUpData.user.id) {
            await (supabase as any).from("referrals").insert({
              referrer_user_id: refRow.user_id,
              referred_user_id: signUpData.user.id,
              code: refCode.toUpperCase(),
            });
          }
        }

        clearAttempts();
        setSignupEmailSent(true);
        toast({
          title: "Account created!",
          description: "Please check your email to verify your account before signing in.",
        });
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password,
        });

        if (error) {
          // Generic message — prevent email enumeration & distinguish unverified
          const isUnverified = error.message.toLowerCase().includes("email not confirmed") ||
            error.message.toLowerCase().includes("not confirmed");
          if (isUnverified) {
            toast({
              title: "Email not verified",
              description: "Please verify your email. Click 'Resend verification email' below.",
              variant: "destructive",
            });
          } else {
            const newLockout = recordAttempt();
            if (newLockout) setLockoutUntil(newLockout);
            toast({
              title: "Invalid credentials",
              description: "The email or password you entered is incorrect.",
              variant: "destructive",
            });
          }
          setLoading(false);
          return;
        }
        clearAttempts();
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      const error = err as Error;
      // Generic message for signup errors too
      toast({
        title: "Sign up failed",
        description: error.message.toLowerCase().includes("password")
          ? error.message
          : "Could not create account. Please try a different email or try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const lockoutMinutes = lockoutUntil ? Math.ceil((lockoutUntil - Date.now()) / 60000) : 0;

  // Reset Password View (after clicking email link)
  if (view === "resetPassword") {
    const newPwdChecks = evaluatePassword(newPassword);
    const newPwdStrength = passwordStrength(newPwdChecks);
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <a href="/" className="inline-block mx-auto mb-4">
              <img src={logo} alt="Fitawrari FX" className="h-14 w-auto mx-auto" />
            </a>
            {resetSuccess ? (
              <>
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-primary" />
                <CardTitle className="font-display text-2xl">Password Updated</CardTitle>
                <CardDescription>Your password has been successfully changed.</CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="font-display text-2xl">Set New Password</CardTitle>
                <CardDescription>Enter your new password below</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {resetSuccess ? (
              <Button className="w-full" onClick={() => navigate(redirectTo)}>
                Continue
              </Button>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 8 chars, mix of cases & number"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {newPassword && <PasswordStrengthMeter checks={newPwdChecks} strength={newPwdStrength} />}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Update Password
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot Password View
  if (view === "forgotPassword") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <a href="/" className="inline-block mx-auto mb-4">
              <img src={logo} alt="Fitawrari FX" className="h-14 w-auto mx-auto" />
            </a>
            <CardTitle className="font-display text-2xl">Reset Password</CardTitle>
            <CardDescription>
              {resetEmailSent
                ? "Check your email for the reset link"
                : "Enter your email to receive a password reset link"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {resetEmailSent ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  If an account exists for <strong>{form.email}</strong>, a reset link has been sent. Please check your inbox and spam folder.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => { setView("signIn"); setResetEmailSent(false); }}
                >
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email Address</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Send Reset Link
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    className="text-primary text-sm font-medium hover:underline"
                    onClick={() => setView("signIn")}
                  >
                    Back to Sign In
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Signup success state
  if (signupEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <CheckCircle className="w-16 h-16 mx-auto text-primary" />
            <h2 className="text-2xl font-display font-bold">Check Your Email</h2>
            <p className="text-muted-foreground">
              We've sent a verification link to <strong>{form.email}</strong>. Click it to activate your account.
            </p>
            <Button variant="outline" className="w-full" onClick={() => { setSignupEmailSent(false); setView("signIn"); }}>
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Sign In / Sign Up View
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <SEO
        title="Sign in — Fitawrari FX"
        description="Sign in or create your Fitawrari FX student account to access mentorship, courses, and tools."
        path="/auth"
        noindex
      />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <a href="/" className="inline-block mx-auto mb-4">
            <img src={logo} alt="Fitawrari FX" className="h-14 w-auto mx-auto" />
          </a>
          <CardTitle className="font-display text-2xl">
            {view === "signUp" ? "Create Account" : "Welcome Back"}
          </CardTitle>
          <CardDescription>
            {view === "signUp"
              ? "Join the Fitawrari FX mentorship program"
              : "Sign in to access your courses"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {lockoutUntil > 0 && (
            <div className="mb-4 p-3 rounded-md bg-destructive/10 border border-destructive/20 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <div className="text-xs text-destructive">
                <p className="font-medium">Too many failed attempts</p>
                <p className="text-destructive/80">Please wait {lockoutMinutes} minute(s) before trying again.</p>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            {view === "signUp" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    placeholder="Enter your full name"
                    value={form.fullName}
                    onChange={handleNameChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="flex gap-2">
                    <CountryCodePicker value={countryCode} onValueChange={setCountryCode} />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="912345678"
                      value={phoneLocal}
                      onChange={handlePhoneChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={view === "signUp" ? "Min 8 chars, mix of cases & number" : "Your password"}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  required
                  minLength={view === "signUp" ? 8 : 6}
                  autoComplete={view === "signUp" ? "new-password" : "current-password"}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {view === "signUp" && form.password && (
                <PasswordStrengthMeter checks={passwordChecks} strength={pwdStrength} />
              )}
            </div>
            <Button type="submit" className="w-full" size="lg" disabled={loading || lockoutUntil > 0}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {view === "signUp" ? "Sign Up" : "Sign In"}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">
              {view === "signUp" ? "Already have an account?" : "Don't have an account?"}
            </span>{" "}
            <button
              className="text-primary font-medium hover:underline"
              onClick={() => setView(view === "signUp" ? "signIn" : "signUp")}
            >
              {view === "signUp" ? "Sign In" : "Sign Up"}
            </button>
          </div>
          {view === "signIn" && (
            <div className="mt-3 flex flex-col items-center gap-2">
              <button
                type="button"
                className="text-sm text-primary font-medium hover:underline"
                onClick={() => setView("forgotPassword")}
              >
                Forgot password?
              </button>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-primary hover:underline"
                onClick={handleResendVerification}
                disabled={loading}
              >
                Resend verification email
              </button>
            </div>
          )}
          <div className="mt-4 text-center">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-muted-foreground">
              <Home className="w-4 h-4 mr-1" /> Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// === Password Strength Meter ===
const PasswordStrengthMeter = ({ checks, strength }: { checks: PasswordChecks; strength: number }) => {
  const labels = ["Very weak", "Weak", "Fair", "Good", "Strong"];
  const colors = [
    "bg-destructive",
    "bg-destructive",
    "bg-amber-500",
    "bg-amber-400",
    "bg-primary",
  ];
  return (
    <div className="space-y-2 pt-1">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${i < strength ? colors[strength] : "bg-muted"}`}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Strength: <span className="font-medium">{labels[strength]}</span>
      </p>
      <ul className="text-xs space-y-1">
        <CheckItem ok={checks.length} label="At least 8 characters" />
        <CheckItem ok={checks.uppercase} label="One uppercase letter" />
        <CheckItem ok={checks.lowercase} label="One lowercase letter" />
        <CheckItem ok={checks.number} label="One number" />
      </ul>
    </div>
  );
};

const CheckItem = ({ ok, label }: { ok: boolean; label: string }) => (
  <li className={`flex items-center gap-1.5 ${ok ? "text-primary" : "text-muted-foreground"}`}>
    {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
    {label}
  </li>
);

export default Auth;
